import { Component, Input } from '@angular/core';
import { filter, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ApiClientConstant, RequestQueryPayload, Table } from '@cureskin/api-client';
import { AppConfig } from '../../../app.config';
import { ConnectionService } from '../../../../services/connection-service';
import { LocalStorage } from '../../../../services/local-storage-service';
import { Broadcaster } from '../../../../components/broadcaster';
import { SupportTicketHistoryComponent } from '../../../patient/support-ticket-history/support-ticket-history.component';
import { ChangeTicketAllocationComponent } from '../../../../components/change-ticket-allocation/change-ticket-allocation.component';
import { DuplicateTicketModalComponent } from '../duplicate-ticket-modal/duplicate-ticket-modal.component';
import { TextViewDialogComponent } from '../../../../components/text-view-dialog';

@Component({
    selector: 'ticket-list', templateUrl: './ticket-list.html', styleUrls: ['./ticket-list.scss'],
    standalone: false
})
export class TicketListComponent {
  @Input('user')
  set userFunction(user: any) {
    if (!user || !user.id) {
      this.user = undefined;
      return;
    }
    this.user = user;
    this.fetchTickets(this.selectedFilter);
  }
  user: Table.User;
  @Input('newUI') newUI: boolean = false;
  tickets: Array<Table.SupportTicket> = [];
  ticketsFetchedSoFar: number = 0;
  ticketsPerFetch: number = 2;
  filteredTickets: Array<Table.SupportTicket> = [];
  disableViewMore: boolean = false;
  selectedFilter: string = 'All'
  isFetching: boolean = false;
  public subscriptions: Subscription[] = [];

  // Duplicate ticket selection properties
  selectedTickets: Array<Table.SupportTicket> = [];
  showDuplicateCheckboxes: boolean = false;

  constructor(private conn: ConnectionService,
    private storage: LocalStorage,
    public appConfig: AppConfig,
    private dialog: MatDialog,
    private broadcaster: Broadcaster) {
  }

  editTicket(ticket: any): void {
    const dialogRef = this.dialog.open(ChangeTicketAllocationComponent, {
      data: {
        allocatedDoctor: ticket.get('allocatedDoctor'),
        allocatedOperator: ticket.get('allocatedOperator'),
        teams: ticket.get('teams'),
        skipReAllocateDoctor: ticket.get('skipReAllocateDoctor'),
      },
    });
    dialogRef.afterClosed().subscribe(async (data: any): Promise<any> => {
      if (!data) return;
      const context = {
        reAllocateDoctor: false,
      };
      try {
        if (!data) return;
        if (data.selectedOperator?.id) ticket.set('allocatedOperator', data.selectedOperator);
        if (data.teams?.length) {
          if (data.teams.includes('doctor')) {
            context.reAllocateDoctor = true;
          }
          ticket.set('teams', data.teams);
        }
        ticket.set('skipReAllocateDoctor', data.skipReAllocateDoctor);
        if (data.selectedDoctor?.id) {
          ticket.set('allocatedDoctor', data.selectedDoctor);
        }
        await ticket.save({}, { context });
        this.broadcaster.broadcast('NOTIFY', { message: 'Allocation updated successfully.' });
      } catch (error) {
        this.broadcaster.broadcast('NOTIFY', { message: error.message || error, type: this.appConfig.Shared.Toast.Type.ERROR });
      }
    });
  }

  async fetchTickets(filter: string, isRefresh: boolean = false): Promise<any> {
    if (this.isFetching) return;
    this.isFetching = true;
    try {
      if (isRefresh) {
        this.tickets = [];
        this.ticketsFetchedSoFar = 0;
        this.disableViewMore = false;
      }
      const filterCondition = this.getFilterCondition(filter);
      const queryOptions: RequestQueryPayload<Table.SupportTicket> = {
        where: { user: this.user, ...filterCondition },
        descending: 'createdAt',
        include: [
          'supportQuestion',
          'supportQuestion.questionLanguageString' as 'supportQuestion',
          'titleLanguageString',
          'allocatedOperator',
          'allocatedDoctor',
          'createdBy',
          'completedBy',
          'completedBy.username' as 'completedBy',
          'SBAR'],
        limit: this.ticketsPerFetch,
      };

      if (!isRefresh) {
        queryOptions.skip = this.ticketsFetchedSoFar;
      }

      const fetchedTickets = await this.conn.fetchSupportTickets(queryOptions);
      this.tickets = [...this.tickets, ...fetchedTickets];
      this.ticketsFetchedSoFar += fetchedTickets.length;
      this.disableViewMore = fetchedTickets.length < this.ticketsPerFetch;
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', { message: `Error fetching tickets: ${error}`, type: this.appConfig.Shared.Toast.Type.ERROR });
    } finally {
      this.isFetching = false;
    }
  }

  openSupportTicketHistory(): void {
    if (!this.user) {
      return;
    }
    this.dialog.open(SupportTicketHistoryComponent, {
      width: '95%',
      data: { user: this.user },
    });
  }

  async openSummary(): Promise<void> {
    if (!this.user) {
      return;
    }
    try {
      const response = await this.conn.getTicketsSummary(this.user);
      const summaryText = response?.data?.data?.summary?.summary ?? '';
      this.dialog.open(TextViewDialogComponent, {
        width: '70%',
        data: { title: 'Tickets Summary Of last 7 days', text: summaryText || 'No summary available.' },
      });
    } catch (error: any) {
      this.broadcaster.broadcast('NOTIFY', {
        message: `Error fetching tickets summary: ${error.message || error}`,
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
    }
  }

  async toggleFilter(filter: string): Promise<void> {
    this.selectedFilter = filter;
    this.tickets = [];
    this.ticketsFetchedSoFar = 0;
    this.disableViewMore = false;
    if (filter === 'Active') {
      this.showDuplicateCheckboxes = true;
    } else {
      this.showDuplicateCheckboxes = false;
    }
    await this.fetchTickets(filter);
  }

  getFilterCondition(filter: string): object {
    const filterCondition: { [key: string]: any } = {};
    if (this.selectedFilter === 'Active') {
      filterCondition.status = 'Pending';
      filterCondition.treeCompleted = true;
      filterCondition.openTime = { $exists: true };
    }
    if (this.selectedFilter === 'Completed') {
      filterCondition.status =  'Completed';
    }

    if(this.selectedFilter === 'Spam') {
      filterCondition.status = 'Pending';
      filterCondition.treeCompleted = false;
      filterCondition.openTime = { $exists: false };
    }
    if(this.selectedFilter === 'All') {
      filterCondition.status = { $in: ['Pending', 'Completed', 'RequestClosure'] }
    }
    return filterCondition;
  }

  async searchTicketByTicketNumber(ticketNumber: string): Promise<any> {
    const trimmedTicketNumber = ticketNumber?.trim();
    if (!trimmedTicketNumber) {
      this.resetToNormalView();
      return;
    }
    await this.performTicketSearch(trimmedTicketNumber);
  }

  onSearchInputChange(value: string): void {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
      this.resetToNormalView();
    }
  }

  private resetToNormalView(): void {
    this.tickets = [];
    this.ticketsFetchedSoFar = 0;
    this.disableViewMore = false;
    this.fetchTickets(this.selectedFilter);
  }

  private async performTicketSearch(ticketNumber: string): Promise<void> {
    try {
      const ticket = await this.fetchSingleTicket(ticketNumber);
      this.handleSearchResults(ticket);
    } catch (error) {
      this.handleSearchError(error);
    }
  }

  private async fetchSingleTicket(ticketNumber: string): Promise<Table.SupportTicket> {
    return await this.conn.fetchSupportTicket({
      where: {
        user: this.user,
        ticketNumber: ticketNumber,
      },
      include: [
        'supportQuestion',
        'supportQuestion.questionLanguageString' as 'supportQuestion',
        'titleLanguageString',
        'allocatedOperator',
        'allocatedDoctor',
        'createdBy',
        'completedBy',
        'completedBy.username' as 'completedBy',
        'SBAR'
      ],
    });
  }

  private handleSearchResults(ticket: Table.SupportTicket | null): void {
    this.tickets = ticket ? [ticket] : [];
    this.disableViewMore = true;
  }

  private handleSearchError(error: any): void {
    this.broadcaster.broadcast('NOTIFY', {
      message: `Error searching for ticket: ${error.message || error}`,
      type: this.appConfig.Shared.Toast.Type.ERROR
    });
    this.tickets = [];
    this.disableViewMore = true;
  }

  cancelDuplicateSelection(): void {
    this.selectedTickets = [];
  }

  isTicketSelected(ticketId: string): boolean {
    return this.selectedTickets.some(t => t.id === ticketId);
  }

  onTicketSelectionChange(ticket: Table.SupportTicket, selected: boolean): void {
    if (selected) {
      if (!this.selectedTickets.find(t => t.id === ticket.id)) {
        this.selectedTickets.push(ticket);
      }
    } else {
      this.selectedTickets = this.selectedTickets.filter(t => t.id !== ticket.id);
    }
  }

  openDuplicateModal(): void {
    if (this.selectedTickets.length < 2) {
      this.broadcaster.broadcast('NOTIFY', {
        message: 'Please select at least 2 tickets to mark as duplicates',
        type: this.appConfig.Shared.Toast.Type.ERROR
      });
      return;
    }

    const dialogRef = this.dialog.open(DuplicateTicketModalComponent, {
      width: '600px',
      data: { tickets: this.selectedTickets },
    });

    dialogRef.afterClosed().subscribe(async (param: any) => {
      try {
        // Reset selection and refresh tickets
        this.selectedTickets = [];
        if (param === 'success') {
          this.broadcaster.broadcast('NOTIFY', {
            message: 'Tickets marked as duplicates successfully',
            type: this.appConfig.Shared.Toast.Type.SUCCESS
          });
          await this.fetchTickets('Active', true);
        }
      } catch (error) {
        this.broadcaster.broadcast('NOTIFY', {
          message: `Error fetching tickets: ${error.message || error}`,
          type: this.appConfig.Shared.Toast.Type.ERROR
        });
      }
    });
  }

  protected readonly apiClientConstant: typeof ApiClientConstant = ApiClientConstant;
}
